package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.AppTooltipText
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.layout.AppHorizontalScrollbar
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.CompanyRef
import br.com.vvrefeicoes.portal.domain.model.ComplaintStatus
import br.com.vvrefeicoes.portal.domain.model.CustomerComplaint
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.allows
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import com.composables.icons.lucide.CircleAlert
import com.composables.icons.lucide.EllipsisVertical
import com.composables.icons.lucide.Eye
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.MessageSquareWarning
import com.composables.icons.lucide.Pencil
import com.composables.icons.lucide.Plus
import com.composables.icons.lucide.Tag
import com.composables.icons.lucide.Trash2
import com.composables.icons.lucide.User
import com.composables.icons.lucide.Users
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_action_edit
import portalvv.shared.generated.resources.operational_action_save
import portalvv.shared.generated.resources.operational_action_view
import portalvv.shared.generated.resources.operational_cancel
import portalvv.shared.generated.resources.operational_complaint_actions
import portalvv.shared.generated.resources.operational_complaint_add
import portalvv.shared.generated.resources.operational_complaint_col_date
import portalvv.shared.generated.resources.operational_complaint_col_description
import portalvv.shared.generated.resources.operational_complaint_col_directorate
import portalvv.shared.generated.resources.operational_complaint_col_manager
import portalvv.shared.generated.resources.operational_complaint_col_nature
import portalvv.shared.generated.resources.operational_complaint_col_owner
import portalvv.shared.generated.resources.operational_complaint_col_restaurant
import portalvv.shared.generated.resources.operational_complaint_col_service
import portalvv.shared.generated.resources.operational_complaint_col_shift
import portalvv.shared.generated.resources.operational_complaint_col_status
import portalvv.shared.generated.resources.operational_complaint_col_supplier
import portalvv.shared.generated.resources.operational_complaint_col_time
import portalvv.shared.generated.resources.operational_complaint_col_treatment
import portalvv.shared.generated.resources.operational_complaint_delete
import portalvv.shared.generated.resources.operational_complaint_delete_message
import portalvv.shared.generated.resources.operational_complaint_delete_title
import portalvv.shared.generated.resources.operational_complaint_empty
import portalvv.shared.generated.resources.operational_complaint_form_title_edit
import portalvv.shared.generated.resources.operational_complaint_indicator_pending
import portalvv.shared.generated.resources.operational_complaint_indicator_top_nature
import portalvv.shared.generated.resources.operational_complaint_indicator_total
import portalvv.shared.generated.resources.operational_complaint_status_closed
import portalvv.shared.generated.resources.operational_complaint_status_in_progress
import portalvv.shared.generated.resources.operational_complaint_status_open
import portalvv.shared.generated.resources.operational_complaint_status_resolved

private val TABLE_WIDTH = 1700.dp

@Composable
fun ComplaintContent(
    unit: CompanyRef,
    month: MonthPeriod,
) {
    val controller = rememberCustomerComplaintController()
    val permissions = rememberSessionPermissions()
    val canEdit = permissions.allows(PermissionKeys.OPERATIONAL_ENTRIES, PermissionAction.EDIT)
    val filters = LocalGlobalFilters.current
    var viewingRecord by remember { mutableStateOf<CustomerComplaint?>(null) }

    LaunchedEffect(controller, unit, month, filters.units) {
        controller.applyUnit(unit.name, filters.units.map { it.name }.toSet())
        controller.applyPeriod(month)
        controller.init()
    }

    if (controller.loading) {
        Box(
            modifier = Modifier.fillMaxWidth().height(240.dp),
            contentAlignment = Alignment.Center,
        ) {
            CircularProgressIndicator()
        }
    } else {
        ComplaintIndicators(controller.records)
        if (canEdit) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
            ) {
                AppButton(
                    text = stringResource(Res.string.operational_complaint_add),
                    onClick = controller::openAdd,
                    leadingIcon = Lucide.Plus,
                )
            }
        }
        ComplaintTable(
            records = controller.records,
            canEdit = canEdit,
            onView = { viewingRecord = it },
            onEdit = controller::openEdit,
            onDelete = controller::requestDelete,
        )
    }

    if (controller.formOpen) {
        ComplaintFormDialog(controller)
    }

    controller.deleteTarget?.let { record ->
        ComplaintDeleteDialog(controller = controller, record = record)
    }

    viewingRecord?.let { record ->
        ComplaintViewDialog(record = record, onDismiss = { viewingRecord = null })
    }
}

@Composable
private fun ComplaintIndicators(records: List<CustomerComplaint>) {
    val pending = records.count { it.status == ComplaintStatus.OPEN || it.status == ComplaintStatus.IN_PROGRESS }
    val topNature = records.groupingBy { it.nature }.eachCount().maxByOrNull { it.value }
    val topNatureLabel = topNature?.let { "${it.key} (${it.value})" } ?: "-"

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space24),
            verticalAlignment = Alignment.Top,
        ) {
            ComplaintIndicator(
                icon = Lucide.MessageSquareWarning,
                iconColor = AppPalette.Primary500,
                iconContainerColor = AppPalette.Primary100,
                label = stringResource(Res.string.operational_complaint_indicator_total),
                value = records.size.toString(),
            )
            ComplaintIndicator(
                icon = Lucide.CircleAlert,
                iconColor = AppPalette.Warning500,
                iconContainerColor = AppPalette.Warning100,
                label = stringResource(Res.string.operational_complaint_indicator_pending),
                value = pending.toString(),
            )
            ComplaintIndicator(
                icon = Lucide.Tag,
                iconColor = AppPalette.Info500,
                iconContainerColor = AppPalette.Info100,
                label = stringResource(Res.string.operational_complaint_indicator_top_nature),
                value = topNatureLabel,
            )
        }
    }
}

@Composable
private fun RowScope.ComplaintIndicator(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    value: String,
) {
    Row(
        modifier = Modifier.weight(1f),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Surface(
            modifier = Modifier.size(36.dp),
            shape = AppShapes.small,
            color = iconContainerColor,
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = iconColor,
                    modifier = Modifier.size(16.dp),
                )
            }
        }
        Column(
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
            horizontalAlignment = Alignment.Start,
        ) {
            Text(
                text = label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = value,
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.onSurface,
            )
        }
    }
}

@Composable
private fun ComplaintTable(
    records: List<CustomerComplaint>,
    canEdit: Boolean,
    onView: (CustomerComplaint) -> Unit,
    onEdit: (CustomerComplaint) -> Unit,
    onDelete: (CustomerComplaint) -> Unit,
) {
    val scrollState = rememberScrollState()

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val tableWidth = maxOf(maxWidth, TABLE_WIDTH)
            Box(
                modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState),
                contentAlignment = Alignment.TopStart,
            ) {
                Column(modifier = Modifier.width(tableWidth)) {
                    ComplaintHeaderRow()
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    if (records.isEmpty()) {
                        EmptyState(stringResource(Res.string.operational_complaint_empty))
                    } else {
                        records.forEachIndexed { index, record ->
                            ComplaintBodyRow(record = record, canEdit = canEdit, onView = onView, onEdit = onEdit, onDelete = onDelete)
                            if (index < records.lastIndex) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                            }
                        }
                    }
                }
            }
        }
    }

    AppHorizontalScrollbar(
        state = scrollState,
        modifier = Modifier.fillMaxWidth().padding(bottom = AppSpacing.space4),
    )
}

@Composable
private fun ComplaintHeaderRow() {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space12),
        horizontalArrangement = Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        HeaderCell(stringResource(Res.string.operational_complaint_col_date), 0.9f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_time), 0.7f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_service), 1.0f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_status), 1.0f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_supplier), 1.2f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_shift), 0.9f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_nature), 1.3f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_description), 2.2f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_treatment), 2.2f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_col_owner), 1.4f, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_complaint_actions), 0.8f, TextAlign.End)
    }
}

@Composable
private fun ComplaintBodyRow(
    record: CustomerComplaint,
    canEdit: Boolean,
    onView: (CustomerComplaint) -> Unit,
    onEdit: (CustomerComplaint) -> Unit,
    onDelete: (CustomerComplaint) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space12),
        horizontalArrangement = Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        BodyCell(dateLabel(record.date), 0.9f)
        BodyCell(record.time, 0.7f)
        BodyCell(record.service, 1.0f)
        Box(
            modifier = Modifier.weight(1.0f).padding(horizontal = AppSpacing.space8),
            contentAlignment = Alignment.TopStart,
        ) {
            StatusBadge(record.status)
        }
        BodyCell(record.supplier, 1.2f)
        BodyCell(record.shift, 0.9f)
        BodyCell(record.nature, 1.3f)
        LongTextCell(record.description, 2.2f)
        LongTextCell(record.treatment, 2.2f)
        BodyCell(record.treatmentOwner, 1.4f)
        Box(
            modifier = Modifier.weight(0.8f).padding(horizontal = AppSpacing.space8),
            contentAlignment = Alignment.CenterEnd,
        ) {
            ComplaintRowActions(
                canEdit = canEdit,
                onView = { onView(record) },
                onEdit = { onEdit(record) },
                onDelete = { onDelete(record) },
            )
        }
    }
}

@Composable
private fun RowScope.HeaderCell(
    text: String,
    weight: Float,
    align: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = align,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun RowScope.BodyCell(
    text: String,
    weight: Float,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun RowScope.LongTextCell(
    text: String,
    weight: Float,
) {
    AppTooltipText(
        text = text,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
    )
}

@Composable
private fun StatusBadge(status: ComplaintStatus) {
    val (containerColor, contentColor) = badgeColors(status)
    AppBadge(
        text = label(status),
        containerColor = containerColor,
        contentColor = contentColor,
    )
}

@Composable
private fun label(status: ComplaintStatus): String =
    when (status) {
        ComplaintStatus.OPEN -> stringResource(Res.string.operational_complaint_status_open)
        ComplaintStatus.IN_PROGRESS -> stringResource(Res.string.operational_complaint_status_in_progress)
        ComplaintStatus.RESOLVED -> stringResource(Res.string.operational_complaint_status_resolved)
        ComplaintStatus.CLOSED -> stringResource(Res.string.operational_complaint_status_closed)
    }

private fun badgeColors(status: ComplaintStatus): Pair<Color, Color> =
    when (status) {
        ComplaintStatus.OPEN -> AppPalette.Warning100 to AppPalette.Warning900
        ComplaintStatus.IN_PROGRESS -> AppPalette.Info100 to AppPalette.Info900
        ComplaintStatus.RESOLVED -> AppPalette.Success100 to AppPalette.Success900
        ComplaintStatus.CLOSED -> AppPalette.Neutral200 to AppPalette.Neutral900
    }

private fun dateLabel(iso: String): String =
    if (iso.length >= 10) {
        "${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}"
    } else {
        iso
    }

private val ComplaintServices = listOf("Desjejum", "Almoço", "Lanche 1", "Lanche 2", "Janta", "Lanche 3", "Ceia")
private val ComplaintShifts = listOf("Manhã", "Tarde", "Noite")

@Composable
private fun ComplaintFormDialog(controller: CustomerComplaintController) {
    val form = controller.form
    AppModalCard(
        title =
            stringResource(
                if (controller.formRecordId == null) {
                    Res.string.operational_complaint_add
                } else {
                    Res.string.operational_complaint_form_title_edit
                },
            ),
        onDismiss = controller::dismissForm,
    ) {
        Text(
            text = stringResource(Res.string.operational_complaint_col_restaurant),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = form.restaurant,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface,
        )
        AppTextField(
            value = form.date,
            onValueChange = controller::updateDate,
            label = stringResource(Res.string.operational_complaint_col_date),
            placeholder = "dd/MM/aaaa",
            isError = controller.dateError,
            onBlur = controller::commitDate,
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = form.time,
            onValueChange = controller::updateTime,
            label = stringResource(Res.string.operational_complaint_col_time),
            placeholder = "HH:mm",
            isError = controller.timeError,
            onBlur = controller::commitTime,
            modifier = Modifier.fillMaxWidth(),
        )
        ComplaintSelect(
            label = stringResource(Res.string.operational_complaint_col_directorate),
            options = controller.directorOptions,
            selected = form.directorate,
            onSelect = { value -> controller.updateForm { it.copy(directorate = value) } },
            leadingIcon = Lucide.User,
        )
        ComplaintSelect(
            label = stringResource(Res.string.operational_complaint_col_manager),
            options = controller.managerOptions,
            selected = form.managerName,
            onSelect = { value -> controller.updateForm { it.copy(managerName = value) } },
            leadingIcon = Lucide.Users,
        )
        ComplaintSelect(
            label = stringResource(Res.string.operational_complaint_col_service),
            options = ComplaintServices,
            selected = form.service,
            onSelect = { value -> controller.updateForm { it.copy(service = value) } },
            leadingIcon = Lucide.Tag,
        )
        ComplaintSelect(
            label = stringResource(Res.string.operational_complaint_col_shift),
            options = ComplaintShifts,
            selected = form.shift,
            onSelect = { value -> controller.updateForm { it.copy(shift = value) } },
            leadingIcon = Lucide.Tag,
        )
        AppTextField(
            value = form.nature,
            onValueChange = { value -> controller.updateForm { it.copy(nature = value) } },
            label = stringResource(Res.string.operational_complaint_col_nature),
            modifier = Modifier.fillMaxWidth(),
        )
        val statusPairs = ComplaintStatus.entries.map { it to label(it) }
        ComplaintSelect(
            label = stringResource(Res.string.operational_complaint_col_status),
            options = statusPairs.map { it.second },
            selected = label(form.status),
            onSelect = { value ->
                statusPairs.firstOrNull { it.second == value }?.let { (status, _) ->
                    controller.updateForm { it.copy(status = status) }
                }
            },
            leadingIcon = Lucide.CircleAlert,
        )
        AppTextField(
            value = form.supplier,
            onValueChange = { value -> controller.updateForm { it.copy(supplier = value) } },
            label = stringResource(Res.string.operational_complaint_col_supplier),
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = form.description,
            onValueChange = { value -> controller.updateForm { it.copy(description = value) } },
            label = stringResource(Res.string.operational_complaint_col_description),
            singleLine = false,
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = form.treatment,
            onValueChange = { value -> controller.updateForm { it.copy(treatment = value) } },
            label = stringResource(Res.string.operational_complaint_col_treatment),
            singleLine = false,
            modifier = Modifier.fillMaxWidth(),
        )
        AppTextField(
            value = form.treatmentOwner,
            onValueChange = { value -> controller.updateForm { it.copy(treatmentOwner = value) } },
            label = stringResource(Res.string.operational_complaint_col_owner),
            modifier = Modifier.fillMaxWidth(),
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.operational_cancel),
                onClick = controller::dismissForm,
                variant = PortalButtonVariant.Text,
            )
            AppButton(
                text = stringResource(Res.string.operational_action_save),
                onClick = controller::submitForm,
                enabled = controller.canSubmitForm,
            )
        }
    }
}

@Composable
private fun ComplaintDeleteDialog(
    controller: CustomerComplaintController,
    record: CustomerComplaint,
) {
    AppModalCard(
        title = stringResource(Res.string.operational_complaint_delete_title),
        onDismiss = controller::dismissDelete,
    ) {
        Text(
            text =
                stringResource(
                    Res.string.operational_complaint_delete_message,
                    dateLabel(record.date),
                    record.time,
                    record.nature,
                ),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.operational_cancel),
                onClick = controller::dismissDelete,
                variant = PortalButtonVariant.Text,
            )
            AppButton(
                text = stringResource(Res.string.operational_complaint_delete),
                onClick = controller::confirmDelete,
                enabled = !controller.deleting,
            )
        }
    }
}

@Composable
private fun ComplaintSelect(
    label: String,
    options: List<String>,
    selected: String,
    onSelect: (String) -> Unit,
    leadingIcon: ImageVector,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        SearchableSelect(
            leadingIcon = leadingIcon,
            options = options,
            selected = selected,
            onSelect = onSelect,
            modifier = Modifier.fillMaxWidth(),
            underlined = true,
        )
    }
}

@Composable
internal fun ComplaintRowActions(
    canEdit: Boolean,
    onView: () -> Unit,
    onEdit: () -> Unit,
    onDelete: () -> Unit = {},
) {
    var expanded by remember { mutableStateOf(false) }
    IconButton(
        onClick = { expanded = true },
        modifier = Modifier.size(28.dp),
    ) {
        Icon(
            imageVector = Lucide.EllipsisVertical,
            contentDescription = stringResource(Res.string.operational_complaint_actions),
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
    DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
        DropdownMenuItem(
            text = { Text(stringResource(Res.string.operational_action_view)) },
            leadingIcon = { Icon(Lucide.Eye, contentDescription = null) },
            onClick = {
                expanded = false
                onView()
            },
        )
        if (canEdit) {
            DropdownMenuItem(
                text = { Text(stringResource(Res.string.operational_action_edit)) },
                leadingIcon = { Icon(Lucide.Pencil, contentDescription = null) },
                onClick = {
                    expanded = false
                    onEdit()
                },
            )
            DropdownMenuItem(
                text = { Text(stringResource(Res.string.operational_complaint_delete)) },
                leadingIcon = { Icon(Lucide.Trash2, contentDescription = null) },
                onClick = {
                    expanded = false
                    onDelete()
                },
            )
        }
    }
}
